import { Box, Button, Skeleton, SxProps, Theme, Typography } from '@mui/material';
import { ReactNode } from 'react';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';

export interface PaginatedListColumn {
  key: string;
  label: ReactNode;
  onClick?: (columnKey: string) => void;
  width?: string | number; // e.g., 'auto', '200px', '2fr', or number for fr units
}

interface PaginatedListProps<T extends { id: string | number }> {
  data: T[];
  columns: PaginatedListColumn[];
  totalCount?: number;
  currentPage?: number;
  isLoading?: boolean;
  pageSize?: number;
  onNextPage?: () => void;
  onPrevPage?: () => void;
  renderListItem?: (item: T, columnKey: string) => ReactNode;
  renderHeader?: (column: PaginatedListColumn) => ReactNode;
  onListRowClick?: (item: T) => void;
  minWidth?: string | number;
  headerStyles?: SxProps<Theme>;
  listStyles?: SxProps<Theme>;
  listItemStyles?: SxProps<Theme>;
  CustomNoDataComponent?: ReactNode;
  showCustomPagination?: boolean;
}
const PaginatedList = <T extends { id: string | number }>(props: PaginatedListProps<T>) => {
  const {
    data,
    columns,
    pageSize = 10,
    headerStyles,
    renderListItem,
    listStyles,
    listItemStyles,
    onListRowClick,
    currentPage = 1,
    totalCount = 0,
    renderHeader,
    onNextPage,
    onPrevPage,
    minWidth,
    isLoading,
    CustomNoDataComponent,
    showCustomPagination = false,
  } = props;

  const isPrevDisabled = currentPage <= 1;
  const isNextDisabled = (currentPage - 1) * pageSize + data.length >= totalCount;
  const getGridTemplateColumns = () => {
    const hasCustomWidths = columns.some((col) => col.width !== undefined);

    if (!hasCustomWidths) {
      return 'repeat(auto-fit, minmax(0, 2fr))';
    }

    return columns
      .map((col) => {
        if (col.width === undefined) {
          return '1fr';
        }
        if (typeof col.width === 'number') {
          return `${col.width}fr`;
        }
        return col.width;
      })
      .join(' ');
  };

  const gridTemplateColumns = getGridTemplateColumns();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        minWidth: minWidth ? minWidth : '700px',
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
        {/* Pagination */}
        {!showCustomPagination && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Typography>
              {(currentPage - 1) * pageSize + 1}-{(currentPage - 1) * pageSize + data.length} of{' '}
              {totalCount}
            </Typography>
            <Button
              sx={{ padding: '0px', minWidth: '0px' }}
              disabled={isPrevDisabled}
              onClick={onPrevPage}
            >
              <KeyboardArrowLeftIcon sx={{ color: isPrevDisabled ? '#17191C59' : '#06164B' }} />
            </Button>
            <Button
              sx={{ padding: '0px', minWidth: '0px' }}
              disabled={isNextDisabled}
              onClick={onNextPage}
            >
              <KeyboardArrowRightIcon sx={{ color: isNextDisabled ? '#17191C59' : '#06164B' }} />
            </Button>
          </Box>
        )}
      </Box>
      {/* Header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#2F736E',
          color: '#F6F8F9',
          fontWeight: 700,
          fontSize: '0.75rem',
          lineHeight: '1rem',
          padding: '0.75rem 0.5rem',
          borderRadius: '0.5rem',
          ...headerStyles,
        }}
      >
        {columns.map((column) => {
          if (renderHeader) {
            return renderHeader(column);
          }
          return <Box key={column.key as string}>{column.label}</Box>;
        })}
      </Box>
      {/* Body */}
      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {Array.from({ length: pageSize }).map((_, index) => (
            <Skeleton
              key={`skeleton-${index}`}
              variant="rectangular"
              height={56}
              sx={{
                borderRadius: '0.5rem',
                width: '100%',
                backgroundColor: '#e6e6e6',
                '&::after': {
                  background:
                    'linear-gradient(90deg, rgba(250, 250, 250, 0.71), rgba(255, 255, 255, 0.81), rgba(250, 250, 250, 0.7))',
                },
              }}
            />
          ))}
        </Box>
      ) : data.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {data.map((item) => {
            return (
              <Box
                key={item.id}
                onClick={() => {
                  if (onListRowClick) {
                    onListRowClick(item);
                  }
                }}
                sx={{
                  display: 'grid',
                  gridTemplateColumns,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.875rem 0.75rem',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '0.5rem',
                  ...listStyles,
                }}
              >
                {columns.map((column) => {
                  if (renderListItem) {
                    return renderListItem(item, column.key);
                  }
                  return (
                    <Box key={column.key as string} sx={listItemStyles}>
                      {item[column.key as keyof T] as ReactNode}
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      ) : CustomNoDataComponent ? (
        <> {CustomNoDataComponent} </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '300px',
            backgroundColor: '#FFFFFF',
            borderRadius: '0.5rem',
            padding: '0.75rem 0.5rem',
          }}
        >
          <Typography color="#6C6C6C">No data found</Typography>
        </Box>
      )}
    </Box>
  );
};

export default PaginatedList;
